import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertCircle, Calculator, CheckCircle, Info, Lightbulb, Scale, Server, Users } from 'lucide-react';

export default function UtilityModelTab({ scaleData, selectedScale, setSelectedScale, utilityMargin, setUtilityMargin, costBasis, setCostBasis, customClubRevenue, setCustomClubRevenue, baseCost, utilityFee, modelComparison, formatCurrency }) {
  return (
    <>
              {/* Concept Explainer */}
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-800">
                    <Lightbulb className="w-5 h-5" />
                    The Utility Model Concept
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-blue-900">
                  <p className="mb-4">
                    <strong>Regulated utilities</strong> (electric, gas, water) operate under a simple, transparent framework:
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Cost Recovery:</strong> All operational costs are documented and recoverable</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Regulated Margin:</strong> Profit is guaranteed but capped (typically 8-15% return)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Transparency:</strong> All operations subject to review and audit</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Trust:</strong> Both parties know exactly what they're paying for</span>
                    </li>
                  </ul>
                  <p className="text-sm text-blue-700 bg-blue-100 p-3 rounded-lg">
                    <strong>For VBB:</strong> Instead of "45% of your revenue," we say "Cost of serving your club + {utilityMargin}% margin."
                    This builds trust and eliminates the perception of exploitation.
                  </p>
                </CardContent>
              </Card>

              {/* Utility Calculator */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Utility Model Calculator
                  </CardTitle>
                  <CardDescription>
                    Adjust parameters to see how different scenarios affect club costs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Scale Selector */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">Platform Scale (Total Users)</Label>
                    <div className="grid grid-cols-5 gap-2">
                      {Object.entries(scaleData).map(([key, data]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedScale(key)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            selectedScale === key 
                              ? 'border-primary bg-primary/5' 
                              : 'border-transparent bg-muted hover:bg-muted/80'
                          }`}
                        >
                          <div className={`w-3 h-3 rounded-full ${data.color} mx-auto mb-2`} />
                          <div className="font-bold text-sm">{data.users}</div>
                          <div className="text-xs text-muted-foreground">{data.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Cost Basis Toggle */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">Cost Basis</Label>
                    <div className="flex gap-2">
                      <Button
                        variant={costBasis === 'direct' ? 'default' : 'outline'}
                        onClick={() => setCostBasis('direct')}
                        className="flex-1"
                      >
                        <Server className="w-4 h-4 mr-2" />
                        Direct Infrastructure Only
                      </Button>
                      <Button
                        variant={costBasis === 'full' ? 'default' : 'outline'}
                        onClick={() => setCostBasis('full')}
                        className="flex-1"
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Full Loaded (Infra + Labor)
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {costBasis === 'direct' 
                        ? 'Servers, databases, CDN, APIs - excludes staff costs'
                        : 'All infrastructure plus proportional engineering, support, and operations labor'}
                    </p>
                  </div>

                  {/* Margin Slider */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-sm font-medium">Utility Margin</Label>
                      <span className="text-2xl font-bold text-primary">{utilityMargin}%</span>
                    </div>
                    <Slider
                      value={[utilityMargin]}
                      onValueChange={(v) => setUtilityMargin(v[0])}
                      min={8}
                      max={25}
                      step={1}
                      className="mb-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>8% (Minimal)</span>
                      <span className="text-green-600 font-medium">12-18% (Recommended)</span>
                      <span>25% (Maximum)</span>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="grid md:grid-cols-3 gap-4 pt-4 border-t">
                    <Card className="bg-slate-100">
                      <CardContent className="pt-4">
                        <p className="text-sm text-muted-foreground">Base Platform Cost</p>
                        <p className="text-2xl font-bold">{formatCurrency(baseCost)}</p>
                        <p className="text-xs text-muted-foreground">/month ({costBasis === 'direct' ? 'infra only' : 'full loaded'})</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-100 border-blue-300">
                      <CardContent className="pt-4">
                        <p className="text-sm text-blue-700">VBB Utility Fee</p>
                        <p className="text-2xl font-bold text-blue-800">{formatCurrency(utilityFee)}</p>
                        <p className="text-xs text-blue-600">/month (cost + {utilityMargin}%)</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-100 border-green-300">
                      <CardContent className="pt-4">
                        <p className="text-sm text-green-700">VBB Profit Margin</p>
                        <p className="text-2xl font-bold text-green-800">{formatCurrency(utilityFee - baseCost)}</p>
                        <p className="text-xs text-green-600">/month guaranteed</p>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>

              {/* Model Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="w-5 h-5" />
                    Business Model Comparison
                  </CardTitle>
                  <CardDescription>
                    Compare different fee structures for a club with ${customClubRevenue.toLocaleString()}/month revenue
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <Label className="text-sm font-medium mb-2 block">Club Monthly Revenue</Label>
                    <div className="flex gap-2 items-center">
                      <span className="text-muted-foreground">$</span>
                      <Input
                        type="number"
                        value={customClubRevenue}
                        onChange={(e) => setCustomClubRevenue(Number(e.target.value))}
                        className="w-32"
                      />
                      <span className="text-muted-foreground">/month</span>
                      <div className="flex gap-1 ml-4">
                        {[5000, 10000, 25000, 50000].map(amount => (
                          <Button 
                            key={amount}
                            variant="outline" 
                            size="sm"
                            onClick={() => setCustomClubRevenue(amount)}
                          >
                            ${(amount/1000)}K
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    {modelComparison.map((model, idx) => (
                      <Card key={idx} className={`${idx === 1 ? 'border-2 border-blue-500 shadow-lg' : ''}`}>
                        {idx === 1 && <Badge className="absolute -top-2 left-4 bg-blue-500">Recommended</Badge>}
                        <CardHeader className="pb-2">
                          <div className={`w-3 h-3 rounded-full ${model.color} mb-2`} />
                          <CardTitle className="text-base">{model.model}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">VBB Receives:</span>
                              <span className="font-bold">{formatCurrency(model.vbbShare)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Club Retains:</span>
                              <span className="font-bold text-green-600">{formatCurrency(model.clubRetains)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground">Effective Rate:</span>
                              <span className="font-medium">{((model.vbbShare / customClubRevenue) * 100).toFixed(1)}%</span>
                            </div>
                          </div>
                          
                          <div className="pt-3 border-t space-y-2">
                            <p className="text-xs font-medium text-green-700">Pros:</p>
                            <ul className="text-xs text-muted-foreground space-y-1">
                              {model.pros.map((pro, i) => (
                                <li key={i} className="flex items-start gap-1">
                                  <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                                  {pro}
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-red-700">Cons:</p>
                            <ul className="text-xs text-muted-foreground space-y-1">
                              {model.cons.map((con, i) => (
                                <li key={i} className="flex items-start gap-1">
                                  <AlertCircle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                                  {con}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Utility Industry Benchmarks */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    Industry Benchmarks: Regulated Utility Returns
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Industry</TableHead>
                        <TableHead>Typical ROE</TableHead>
                        <TableHead>Regulatory Body</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Electric Utilities</TableCell>
                        <TableCell>9-11%</TableCell>
                        <TableCell>State PUCs</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Stable, low-risk returns</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Natural Gas Distribution</TableCell>
                        <TableCell>9-10%</TableCell>
                        <TableCell>FERC / State PUCs</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Similar to electric</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Water Utilities</TableCell>
                        <TableCell>9-12%</TableCell>
                        <TableCell>State PUCs</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Capital-intensive</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Telecom (regulated)</TableCell>
                        <TableCell>10-14%</TableCell>
                        <TableCell>FCC</TableCell>
                        <TableCell className="text-sm text-muted-foreground">Higher due to tech investment</TableCell>
                      </TableRow>
                      <TableRow className="bg-blue-50">
                        <TableCell className="font-medium">VBB Platform (proposed)</TableCell>
                        <TableCell className="font-bold text-blue-600">12-18%</TableCell>
                        <TableCell>Club Agreement</TableCell>
                        <TableCell className="text-sm text-blue-700">Tech + service premium</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <p className="text-sm text-muted-foreground mt-4">
                    <strong>Why 12-18%?</strong> Higher than traditional utilities because: (1) technology platforms require continuous R&D investment, 
                    (2) software has higher obsolescence risk, (3) customer service is more intensive than utility metering.
                  </p>
                </CardContent>
              </Card>
    </>
  );
}
